import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import { useProductOptionContext } from '../../context/ProductOptionProvider';
import { buildConfirmButtonList, createOrderSheetAPI } from '../../core/confirm-button.core';
import { createOrderItemList } from '../../core/order-item-creator.core';
import { useConfirmButtonLog } from '../../log/useConfirmButtonLog';
import { CommonConfig, CreateOrderSheetArgs, ProductOptionConfig } from '../../type/common.type';
import {
  getSessionStoragePurchasedItems,
  removeSessionStoragePurchasedItems,
} from '../../util/executors/purchased-storage-executor';
import { productMetadataBuilder } from '../../util/transformers/product-metadata-transformer';

interface Props {
  finalPrice: number;
  context: CommonConfig;
  config: ProductOptionConfig;
  onClose?: () => void;
}

export const useConfirmButton = ({ finalPrice, context, config, onClose }: Props) => {
  const router = useRouter();
  const queryFrom = Array.isArray(router.query.from) ? router.query.from[0] : router.query.from;

  const confirmButtonLogger = useConfirmButtonLog();
  const { state } = useProductOptionContext();

  const productMetadata = productMetadataBuilder(state.option.info, config);

  const confirmButtonList = useMemo(
    () =>
      buildConfirmButtonList({
        option: state.option.info,
        finalPrice,
        queryFrom,
        itemCardList: state.item_cards.list,
        context: {
          ...config?.confirm_button,
          is_logged_in: context.is_logged_in,
          is_zigzag_app: context.is_zigzag_app,
        },
        logger: confirmButtonLogger,
        config: {
          onClose,
        },
      }),
    [
      state.option.info,
      finalPrice,
      state.item_cards.list,
      config?.confirm_button,
      context.is_logged_in,
      context.is_zigzag_app,
      confirmButtonLogger,
      queryFrom,
      onClose,
    ],
  );

  const createOrderFromSession = async () => {
    const purchasedItems = getSessionStoragePurchasedItems(productMetadata.catalog_product_id);
    if (purchasedItems) {
      const items = JSON.parse(purchasedItems);
      await createOrderSheetAPI(items);
      removeSessionStoragePurchasedItems(productMetadata.catalog_product_id);
    }
  };

  const order_item_list = useMemo((): CreateOrderSheetArgs => {
    const created_order_item_list = createOrderItemList({
      items: state.item_cards.list,
      bogo_info: productMetadata.bogo_info,
      funnelType: context?.funnelType ?? queryFrom,
      promotion_info: productMetadata.promotion_info,
      first_order_discount: productMetadata.first_order_discount,
      is_zigzin_matching_product: productMetadata.is_zigzin_matching_product,
    });

    return {
      order_item_list: created_order_item_list,
      from: 'product',
      catalog_product_id: state.option.info?.catalog_product?.id,
    };
  }, [state.item_cards, productMetadata]);

  useEffect(() => {
    if (context.is_logged_in) {
      createOrderFromSession();
    }
  }, [context.is_logged_in]);

  return {
    payload: order_item_list,
    buttons: confirmButtonList,
  };
};
